import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Users, Search, ArrowLeft, Eye, ShieldCheck, ShieldX, ShieldAlert, Loader2,
  Pencil, Trash2, GitMerge, AlertTriangle
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getAdminBuyers, getAdminBuyer, updateBuyerStatus, editBuyer, deleteBuyer, mergeBuyers, findBuyerDuplicates } from '../../lib/api';
import { Toaster, toast } from 'sonner';

const ConfirmModal = ({ open, title, message, warning, onConfirm, onCancel, confirmLabel = 'Confirm', danger }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-[#0A0A0A] border border-white/10 w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="confirm-modal">
        <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
        <p className="text-white/60 text-sm mb-4">{message}</p>
        {warning && <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-xs mb-4"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{warning}</span></div>}
        <div className="flex gap-3 justify-end">
          <button onClick={onCancel} className="px-4 py-2 text-sm text-white/60 hover:text-white" data-testid="confirm-cancel-btn">Cancel</button>
          <button onClick={onConfirm} className={`px-4 py-2 text-sm font-medium ${danger ? 'bg-red-500/20 border border-red-500/40 text-red-400 hover:bg-red-500/30' : 'bg-[#00D4FF]/20 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/30'}`} data-testid="confirm-action-btn">{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

// --- Buyer Detail ---
export const AdminBuyerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useApp();
  const [buyer, setBuyer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [duplicates, setDuplicates] = useState(null);
  const [dupeLoading, setDupeLoading] = useState(false);

  const fetchBuyer = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAdminBuyer(id);
      setBuyer(res.data);
    } catch (err) {
      toast.error(t('adminBuyers.failedLoad'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { fetchBuyer(); }, [fetchBuyer]);

  const handleStatusChange = async (role) => {
    try {
      await updateBuyerStatus(id, { role });
      toast.success(t('adminBuyers.statusUpdated'));
      fetchBuyer();
    } catch (err) {
      toast.error(t('adminBuyers.failedUpdate'));
    }
  };

  const handleSaveEdit = async () => {
    try {
      setActionLoading(true);
      await editBuyer(id, editForm);
      toast.success('Buyer updated');
      setEditing(false);
      fetchBuyer();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setActionLoading(true);
      await deleteBuyer(id);
      toast.success('Buyer deleted');
      navigate('/admin/buyers');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setActionLoading(false);
      setConfirm(null);
    }
  };

  const handleFindDuplicates = async () => {
    try {
      setDupeLoading(true);
      const res = await findBuyerDuplicates(id);
      setDuplicates(res.data);
      if (res.data.length === 0) toast.info('No duplicates found');
    } catch (err) {
      toast.error('Failed to find duplicates');
    } finally {
      setDupeLoading(false);
    }
  };

  const handleMerge = async (secondaryId) => {
    try {
      setActionLoading(true);
      await mergeBuyers({ primary_id: id, secondary_ids: [secondaryId] });
      toast.success('Records merged successfully');
      setDuplicates(null);
      fetchBuyer();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Merge failed');
    } finally {
      setActionLoading(false);
      setConfirm(null);
    }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#00D4FF]" /></div>;
  if (!buyer) return <div className="p-8 text-center text-white/60">{t('adminBuyers.buyerNotFound')}</div>;

  const roleColors = {
    buyer_approved: 'bg-[#10B981]/20 text-[#10B981]',
    buyer_pending: 'bg-[#F59E0B]/20 text-[#F59E0B]',
    buyer_suspended: 'bg-red-500/20 text-red-400',
  };

  return (
    <div className="p-6 lg:p-8" data-testid="admin-buyer-detail">
      <Toaster position="top-right" theme="dark" />
      <button onClick={() => navigate('/admin/buyers')} className="flex items-center gap-2 text-white/60 hover:text-white mb-6" data-testid="back-to-buyers">
        <ArrowLeft className="w-4 h-4" /> {t('adminBuyers.backToBuyers')}
      </button>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="buyer-actions">
        {!editing && (
          <button onClick={() => { setEditing(true); setEditForm({ company_name: buyer.company_name, contact_name: buyer.contact_name, email: buyer.email, whatsapp: buyer.whatsapp || '', country: buyer.country || '', business_type: buyer.business_type || '' }); }} className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 text-white/70 text-sm hover:text-white" data-testid="btn-edit-buyer">
            <Pencil className="w-4 h-4" />Edit
          </button>
        )}
        <button onClick={handleFindDuplicates} disabled={dupeLoading} className="flex items-center gap-2 px-3 py-2 bg-purple-500/10 border border-purple-500/30 text-purple-400 text-sm hover:bg-purple-500/20" data-testid="btn-find-duplicates">
          <GitMerge className="w-4 h-4" />{dupeLoading ? 'Searching...' : 'Find Duplicates'}
        </button>
        <button onClick={() => setConfirm({ type: 'delete', title: 'Delete Buyer', message: `Permanently delete buyer "${buyer.company_name}"? Their orders and quotes will NOT be deleted.`, confirmLabel: 'Delete Forever', danger: true })} className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/20" data-testid="btn-delete-buyer">
          <Trash2 className="w-4 h-4" />Delete
        </button>
      </div>

      {/* Edit form */}
      {editing && (
        <div className="card-dark p-5 mb-6 space-y-3" data-testid="buyer-edit-form">
          <h3 className="font-semibold text-sm text-white/60 uppercase">Edit Buyer</h3>
          {['company_name', 'contact_name', 'email', 'whatsapp', 'country', 'business_type'].map(f => (
            <div key={f}>
              <label className="text-xs text-white/40 block mb-1">{f.replace(/_/g, ' ')}</label>
              <input value={editForm[f] || ''} onChange={e => setEditForm(p => ({ ...p, [f]: e.target.value }))} className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white text-sm" data-testid={`edit-field-${f}`} />
            </div>
          ))}
          <div className="flex gap-2">
            <button onClick={handleSaveEdit} disabled={actionLoading} className="px-4 py-2 bg-[#00D4FF]/20 border border-[#00D4FF]/40 text-[#00D4FF] text-sm font-medium" data-testid="btn-save-edit">Save</button>
            <button onClick={() => setEditing(false)} className="px-4 py-2 text-white/60 text-sm">Cancel</button>
          </div>
        </div>
      )}

      {/* Duplicates panel */}
      {duplicates && duplicates.length > 0 && (
        <div className="card-dark p-5 mb-6 border-purple-500/20" data-testid="duplicates-panel">
          <h3 className="font-semibold text-sm text-purple-400 uppercase mb-3 flex items-center gap-2"><GitMerge className="w-4 h-4" />Potential Duplicates ({duplicates.length})</h3>
          <div className="space-y-2">
            {duplicates.map(d => (
              <div key={d.id} className="flex items-center justify-between p-3 bg-white/5 border border-white/5">
                <div className="text-sm">
                  <span className="text-white font-medium">{d.company_name}</span>
                  <span className="text-white/40 ml-2">{d.email}</span>
                  <span className="text-white/30 ml-2">{d.country}</span>
                </div>
                <button onClick={() => setConfirm({ type: 'merge', title: 'Merge Records', message: `Merge "${d.company_name}" into "${buyer.company_name}"? The duplicate will be deleted and its orders reassigned.`, confirmLabel: 'Merge', mergeId: d.id })} className="px-3 py-1 text-xs bg-purple-500/20 border border-purple-500/30 text-purple-400 hover:bg-purple-500/30" data-testid={`merge-btn-${d.id}`}>
                  Merge into this
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Info */}
        <div className="lg:col-span-2 card-dark p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold">{buyer.company_name}</h2>
            <span className={`px-3 py-1 text-xs font-medium ${roleColors[buyer.role] || 'bg-white/10 text-white/60'}`} data-testid="buyer-role">
              {t(`buyerRole.${buyer.role}`) || buyer.role?.replace('buyer_', '').toUpperCase()}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div><span className="text-white/50">{t('adminBuyers.contact')}:</span> <span className="ml-2">{buyer.contact_name}</span></div>
            <div><span className="text-white/50">{t('adminBuyers.email')}:</span> <span className="ml-2 text-[#00D4FF]">{buyer.email}</span></div>
            <div><span className="text-white/50">{t('adminBuyers.country')}:</span> <span className="ml-2">{buyer.country || '-'}</span></div>
            <div><span className="text-white/50">{t('adminBuyers.whatsapp')}:</span> <span className="ml-2">{buyer.whatsapp || '-'}</span></div>
            <div><span className="text-white/50">{t('adminBuyers.businessType')}:</span> <span className="ml-2">{buyer.business_type || '-'}</span></div>
            <div><span className="text-white/50">{t('adminBuyers.created')}:</span> <span className="ml-2">{new Date(buyer.created_at).toLocaleDateString()}</span></div>
          </div>
          {buyer.interested_categories?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <span className="text-white/50 text-sm">{t('adminBuyers.categories')}:</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {buyer.interested_categories.map(c => (
                  <span key={c} className="px-2 py-1 text-xs bg-[#00D4FF]/10 text-[#00D4FF]">{c}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Stats + Actions */}
        <div className="space-y-4">
          <div className="card-dark p-6">
            <div className="text-white/50 text-sm mb-1">{t('adminBuyers.totalOrders')}</div>
            <div className="text-2xl font-bold font-mono text-[#00D4FF]">{buyer.order_count || 0}</div>
          </div>
          <div className="card-dark p-6">
            <div className="text-white/50 text-sm mb-1">{t('adminBuyers.totalSpent')}</div>
            <div className="text-2xl font-bold font-mono text-[#10B981]">${(buyer.total_spent || 0).toLocaleString()}</div>
          </div>
          <div className="card-dark p-4 space-y-2">
            <div className="text-white/50 text-xs uppercase tracking-wider mb-2">{t('adminBuyers.actions')}</div>
            {buyer.role !== 'buyer_approved' && (
              <button onClick={() => handleStatusChange('buyer_approved')} className="w-full flex items-center gap-2 px-3 py-2 text-sm bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/20" data-testid="approve-buyer-btn">
                <ShieldCheck className="w-4 h-4" /> {t('adminBuyers.approve')}
              </button>
            )}
            {buyer.role !== 'buyer_suspended' && (
              <button onClick={() => handleStatusChange('buyer_suspended')} className="w-full flex items-center gap-2 px-3 py-2 text-sm bg-red-500/10 text-red-400 hover:bg-red-500/20" data-testid="suspend-buyer-btn">
                <ShieldX className="w-4 h-4" /> {t('adminBuyers.suspend')}
              </button>
            )}
            {buyer.role !== 'buyer_pending' && (
              <button onClick={() => handleStatusChange('buyer_pending')} className="w-full flex items-center gap-2 px-3 py-2 text-sm bg-[#F59E0B]/10 text-[#F59E0B] hover:bg-[#F59E0B]/20" data-testid="set-pending-btn">
                <ShieldAlert className="w-4 h-4" /> {t('adminBuyers.setPending')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        warning={confirm?.warning}
        confirmLabel={confirm?.confirmLabel}
        danger={confirm?.danger}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm?.type === 'delete') handleDelete();
          else if (confirm?.type === 'merge') handleMerge(confirm.mergeId);
        }}
      />
    </div>
  );
};

// --- Buyers List ---
export const AdminBuyers = () => {
  const navigate = useNavigate();
  const { t } = useApp();
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const fetchBuyers = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const res = await getAdminBuyers(params);
      setBuyers(res.data);
    } catch (err) {
      toast.error(t('adminBuyers.failedLoadBuyers'));
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, t]);

  useEffect(() => { fetchBuyers(); }, [fetchBuyers]);

  const roleColors = {
    buyer_approved: 'bg-[#10B981]/20 text-[#10B981]',
    buyer_pending: 'bg-[#F59E0B]/20 text-[#F59E0B]',
    buyer_suspended: 'bg-red-500/20 text-red-400',
  };

  return (
    <div className="p-6 lg:p-8" data-testid="admin-buyers-page">
      <Toaster position="top-right" theme="dark" />
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <Users className="w-6 h-6 text-[#00D4FF]" />
            {t('adminBuyers.title')}
          </h1>
          <p className="text-white/50 text-sm mt-1">{buyers.length} {t('adminBuyers.buyers')}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('adminBuyers.searchPlaceholder')}
            className="w-full pl-10 pr-4 py-2 bg-[#0A0A0A] border border-white/10 focus:border-[#00D4FF] text-sm"
            data-testid="buyer-search"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-3 py-2 bg-[#0A0A0A] border border-white/10 text-sm min-w-[160px]"
          data-testid="buyer-role-filter"
        >
          <option value="">{t('adminBuyers.allRoles')}</option>
          <option value="buyer_approved">{t('adminBuyers.approved')}</option>
          <option value="buyer_pending">{t('adminBuyers.pending')}</option>
          <option value="buyer_suspended">{t('adminBuyers.suspended')}</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="card-dark p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#00D4FF]" /></div>
      ) : buyers.length === 0 ? (
        <div className="card-dark p-8 text-center text-white/60" data-testid="no-buyers">{t('adminBuyers.noBuyers')}</div>
      ) : (
        <div className="card-dark overflow-hidden" data-testid="buyers-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#0A0A0A] border-b border-white/10">
                  <th className="p-4 text-left text-xs font-semibold text-white/60 uppercase">{t('adminBuyers.company')}</th>
                  <th className="p-4 text-left text-xs font-semibold text-white/60 uppercase">{t('adminBuyers.contact')}</th>
                  <th className="p-4 text-left text-xs font-semibold text-white/60 uppercase">{t('adminBuyers.email')}</th>
                  <th className="p-4 text-left text-xs font-semibold text-white/60 uppercase">{t('adminBuyers.country')}</th>
                  <th className="p-4 text-center text-xs font-semibold text-white/60 uppercase">{t('adminBuyers.status')}</th>
                  <th className="p-4 text-left text-xs font-semibold text-white/60 uppercase">{t('adminBuyers.created')}</th>
                  <th className="p-4 text-center text-xs font-semibold text-white/60 uppercase">{t('adminBuyers.action')}</th>
                </tr>
              </thead>
              <tbody>
                {buyers.map((buyer) => (
                  <tr key={buyer.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`buyer-row-${buyer.id}`}>
                    <td className="p-4 font-medium">{buyer.company_name}</td>
                    <td className="p-4 text-white/70">{buyer.contact_name}</td>
                    <td className="p-4 text-[#00D4FF] text-sm">{buyer.email}</td>
                    <td className="p-4 text-white/60">{buyer.country || '-'}</td>
                    <td className="p-4 text-center">
                      <span className={`px-2 py-1 text-xs font-medium ${roleColors[buyer.role] || 'bg-white/10 text-white/60'}`}>
                        {t(`buyerRole.${buyer.role}`) || buyer.role?.replace('buyer_', '').toUpperCase()}
                      </span>
                    </td>
                    <td className="p-4 text-white/50 text-sm">{new Date(buyer.created_at).toLocaleDateString()}</td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => navigate(`/admin/buyers/${buyer.id}`)}
                        className="p-2 text-white/40 hover:text-[#00D4FF] transition-colors"
                        data-testid={`view-buyer-${buyer.id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
